import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Search, X } from 'lucide-react';
import { orgsApi } from '@/api/orgs';
import { cn } from '@/utils/helpers';
import type { OrgListItem } from '@/types';

/** Minimal shape the picker actually needs — not a stand-in for the full OrgListItem. */
export type OrgOption = Pick<OrgListItem, 'id' | 'name'> & { slug?: string | null };

function toOrgOption(org: Pick<OrgListItem, 'id' | 'name' | 'slug'>): OrgOption {
  return { id: org.id, name: org.name, slug: org.slug };
}

function orgSecondary(org: { slug?: string | null; id: string }): string {
  return org.slug || `${org.id.slice(0, 8)}…`;
}

function orgLabel(org: { name: string; slug?: string | null; id: string }): string {
  const secondary = orgSecondary(org);
  return secondary ? `${org.name} (${secondary})` : org.name;
}

interface OrgPickerProps {
  value: string | null;
  onChange: (orgId: string | null) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
}

export default function OrgPicker({ value, onChange, label, hint, disabled }: OrgPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const { data: selectedOrg } = useQuery({
    queryKey: ['org-picker-selected', value],
    queryFn: () => orgsApi.get(value!).then(toOrgOption),
    enabled: !!value,
    staleTime: 60_000,
  });

  const { data: listResult, isLoading } = useQuery({
    queryKey: ['org-picker-list', debouncedSearch],
    queryFn: () =>
      orgsApi.list({
        search: debouncedSearch || undefined,
        perPage: 40,
      }),
    enabled: open,
    staleTime: 30_000,
  });

  const items: OrgOption[] = (listResult?.items ?? []).map(toOrgOption);
  const visibleItems = mergeSelectedOrg(items, value, selectedOrg);

  const openPicker = () => {
    if (disabled) return;
    setSearch('');
    setDebouncedSearch('');
    setOpen(true);
  };

  const selectOrg = (orgId: string | null) => {
    onChange(orgId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="w-full" ref={containerRef}>
      {label ? (
        <label className="block text-sm font-medium text-ink-secondary mb-1.5">{label}</label>
      ) : null}

      <div className="relative">
        {!open ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={openPicker}
              disabled={disabled}
              className={cn(
                'flex-1 h-10 px-3 rounded border border-navy-border bg-navy-deeper text-sm text-start',
                'flex items-center justify-between gap-2 transition-colors',
                'hover:border-navy-border/80 focus:outline-none focus:ring-2 focus:ring-gold/40',
                disabled && 'opacity-60 cursor-not-allowed'
              )}
            >
              <span className={value && selectedOrg ? 'text-ink-primary' : 'text-ink-muted'}>
                {value && selectedOrg ? orgLabel(selectedOrg) : '— none —'}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
            </button>
            {value ? (
              <button
                type="button"
                onClick={() => onChange(null)}
                disabled={disabled}
                aria-label="Clear organization"
                className="h-10 w-10 rounded border border-navy-border bg-navy-deeper text-ink-muted hover:text-ink-primary hover:border-navy-border/80"
              >
                <X className="h-4 w-4 mx-auto" />
              </button>
            ) : null}
          </div>
        ) : (
          <div className="rounded border border-navy-border bg-navy-deeper shadow-lg">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-navy-border">
              <Search className="h-4 w-4 text-ink-muted shrink-0" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search organizations by name…"
                className="w-full bg-transparent text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none"
              />
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
              <li>
                <button
                  type="button"
                  onClick={() => selectOrg(null)}
                  className={cn(
                    'w-full px-3 py-2 text-start text-sm hover:bg-white/[0.04]',
                    !value ? 'text-indigo-300' : 'text-ink-muted'
                  )}
                >
                  — none —
                </button>
              </li>
              {isLoading ? (
                <li className="px-3 py-2 text-xs text-ink-muted">Loading organizations…</li>
              ) : visibleItems.length === 0 ? (
                <li className="px-3 py-2 text-xs text-ink-muted">No organizations found</li>
              ) : (
                visibleItems.map((org) => (
                  <li key={org.id}>
                    <button
                      type="button"
                      onClick={() => selectOrg(org.id)}
                      className={cn(
                        'w-full px-3 py-2 text-start hover:bg-white/[0.04]',
                        value === org.id ? 'bg-indigo-500/10' : ''
                      )}
                    >
                      <div className="text-sm text-ink-primary">{org.name}</div>
                      <div className="text-xs text-ink-muted">{orgSecondary(org)}</div>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>

      {hint ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

function mergeSelectedOrg(
  items: OrgOption[],
  value: string | null,
  selectedOrg: OrgOption | undefined
): OrgOption[] {
  if (!value || !selectedOrg || items.some((item) => item.id === value)) {
    return items;
  }
  return [selectedOrg, ...items];
}

export function PartnerNoOrgWarning({ show }: { show: boolean }) {
  if (!show) return null;

  return (
    <div className="flex gap-2 rounded border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
      <span className="shrink-0">⚠</span>
      <p>
        This partner has no JumpStart organization yet — they won&apos;t be able to see their partner
        console until it&apos;s linked.
      </p>
    </div>
  );
}
